import {
  HHBox,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Theme, useTheme } from '@mui/material';
import { OUTCOME_CLASS_LABELS } from '../constants/partner-data-gateway.constant';
import { OutcomeClass, OUTCOME_CLASSES } from '../types';
import { formatCount } from '../utils/format.utils';
import { describeOutcomeCounts } from '../utils/outcome-class.utils';
import { getToneColors } from '../utils/status-style.utils';
import {
  describeWeeklyOutcomes,
  formatWeekStart,
  sumWeeklyOutcomes,
  WeeklyOutcomeBucket,
} from '../utils/weekly-outcomes.utils';
import { SectionTitle } from './stat-row';
import { SurfaceCard } from './surface-card';

export const WEEKLY_OUTCOMES_CHART_TEST_ID = 'pdg-weekly-outcomes-chart';
export const WEEKLY_OUTCOMES_FIGURE_TEST_ID = 'pdg-weekly-outcomes-figure';
export const WEEKLY_OUTCOMES_LEGEND_TEST_ID = 'pdg-weekly-outcomes-legend';
export const WEEKLY_OUTCOMES_EMPTY_TEST_ID = 'pdg-weekly-outcomes-empty';
export const weeklyBarTestId = (weekStart: string): string =>
  `pdg-weekly-bar-${weekStart}`;

export const WEEKLY_OUTCOMES_EMPTY_TEXT = 'No files in this period.';

export const weeklyOutcomesTitle = (sampleCount: number): string =>
  `Files per week, last ${formatCount(sampleCount)} ${
    sampleCount === 1 ? 'file' : 'files'
  }`;

const SLOT_WIDTH = 40;
const BAR_WIDTH = 24;
const BAR_AREA_HEIGHT = 120;
const LABEL_HEIGHT = 18;
const SWATCH_PX = 10;

/**
 * Chart fills. `FAILED` and `REJECTED` share the `bad` chip tone, which is
 * right for a chip but would give two chart classes one colour, so the chart
 * resolves its own: rejected reads as neutral (it is a setup problem, not a
 * pipeline fault) and in flight as the sand `info` surface (not finished).
 * The legend and the image summary carry the meaning; colour is a hint.
 */
const fillFor = (theme: Theme, outcomeClass: OutcomeClass): string => {
  const fills: Record<OutcomeClass, string> = {
    completed: getToneColors(theme, 'ok').fg,
    partial: getToneColors(theme, 'warn').fg,
    failed: getToneColors(theme, 'bad').fg,
    rejected: theme.palette.text.secondary,
    inFlight: theme.palette.info.light,
  };

  return fills[outcomeClass];
};

interface BarProps {
  bucket: WeeklyOutcomeBucket;
  index: number;
  maxTotal: number;
}

/** One stacked bar: segments bottom-up in `OUTCOME_CLASSES` order. */
const Bar = ({ bucket, index, maxTotal }: BarProps): JSX.Element => {
  const theme = useTheme();
  const x = index * SLOT_WIDTH + (SLOT_WIDTH - BAR_WIDTH) / 2;
  const unit = BAR_AREA_HEIGHT / maxTotal;
  let stackedHeight = 0;
  const filesText = `${bucket.total} ${bucket.total === 1 ? 'file' : 'files'}`;
  const detail =
    bucket.total === 0 ? '' : ` (${describeOutcomeCounts(bucket.counts)})`;

  return (
    <g data-testid={weeklyBarTestId(bucket.weekStart)}>
      <title>{`Week of ${formatWeekStart(bucket.weekStart)}: ${filesText}${detail}`}</title>
      {OUTCOME_CLASSES.map(outcomeClass => {
        const count = bucket.counts[outcomeClass];

        if (count === 0) {
          return null;
        }

        const height = count * unit;
        stackedHeight += height;

        return (
          <rect
            key={outcomeClass}
            data-outcome-class={outcomeClass}
            x={x}
            y={BAR_AREA_HEIGHT - stackedHeight}
            width={BAR_WIDTH}
            height={height}
            fill={fillFor(theme, outcomeClass)}
          />
        );
      })}
      <text
        x={x + BAR_WIDTH / 2}
        y={BAR_AREA_HEIGHT + LABEL_HEIGHT - 4}
        textAnchor="middle"
        fontSize={10}
        fill={theme.palette.text.secondary}
      >
        {formatWeekStart(bucket.weekStart)}
      </text>
    </g>
  );
};

interface LegendProps {
  totals: WeeklyOutcomeBucket['counts'];
}

const Legend = ({ totals }: LegendProps): JSX.Element => {
  const theme = useTheme();

  return (
    <HHStack
      direction="row"
      gap={3}
      flexWrap="wrap"
      data-testid={WEEKLY_OUTCOMES_LEGEND_TEST_ID}
    >
      {OUTCOME_CLASSES.map(outcomeClass => (
        <HHStack key={outcomeClass} direction="row" alignItems="center" gap={1}>
          <HHBox
            aria-hidden
            width={SWATCH_PX}
            height={SWATCH_PX}
            borderRadius={0.5}
            bgcolor={fillFor(theme, outcomeClass)}
          />
          <HHTypography
            hhVariant="variant-bypass"
            variant="caption"
            color="text.secondary"
          >
            {OUTCOME_CLASS_LABELS[outcomeClass]}
          </HHTypography>
          <HHTypography
            hhVariant="variant-bypass"
            variant="caption"
            sx={{ fontWeight: 600 }}
          >
            {formatCount(totals[outcomeClass])}
          </HHTypography>
        </HHStack>
      ))}
    </HHStack>
  );
};

interface WeeklyOutcomesChartProps {
  /** Oldest first, as `bucketWeeklyOutcomes` returns them. */
  buckets: WeeklyOutcomeBucket[];
  /** How many files the buckets were built from; names the card. */
  sampleCount: number;
}

/**
 * Stacked bars, one per week, one segment per outcome class. The SVG is
 * decoration: the wrapper is the accessible `img` and its label is the
 * one-sentence summary, with per-week detail in each bar's `<title>`.
 */
export const WeeklyOutcomesChart = ({
  buckets,
  sampleCount,
}: WeeklyOutcomesChartProps): JSX.Element => {
  const maxTotal = Math.max(1, ...buckets.map(bucket => bucket.total));
  const totals = sumWeeklyOutcomes(buckets);
  const isEmpty = buckets.every(bucket => bucket.total === 0);
  const width = buckets.length * SLOT_WIDTH;
  const height = BAR_AREA_HEIGHT + LABEL_HEIGHT;

  return (
    <SurfaceCard
      paddingX={5}
      paddingY={4}
      data-testid={WEEKLY_OUTCOMES_CHART_TEST_ID}
    >
      <HHStack gap={3}>
        <SectionTitle>{weeklyOutcomesTitle(sampleCount)}</SectionTitle>
        {isEmpty ? (
          <HHTypography
            hhVariant="variant-bypass"
            variant="body2"
            color="text.secondary"
            data-testid={WEEKLY_OUTCOMES_EMPTY_TEST_ID}
          >
            {WEEKLY_OUTCOMES_EMPTY_TEXT}
          </HHTypography>
        ) : (
          <>
            <HHBox
              role="img"
              aria-label={describeWeeklyOutcomes(buckets)}
              data-testid={WEEKLY_OUTCOMES_FIGURE_TEST_ID}
            >
              <svg
                aria-hidden="true"
                viewBox={`0 0 ${width} ${height}`}
                width="100%"
                height={height}
                preserveAspectRatio="none"
              >
                {buckets.map((bucket, index) => (
                  <Bar
                    key={bucket.weekStart}
                    bucket={bucket}
                    index={index}
                    maxTotal={maxTotal}
                  />
                ))}
              </svg>
            </HHBox>
            <Legend totals={totals} />
          </>
        )}
      </HHStack>
    </SurfaceCard>
  );
};
